import {
  ChoicePicker,
  ChoicePickerPanel,
  ChoicePickerTrigger,
  type ChoiceFilter,
  type ChoiceItem,
} from "@/components/molecules/choice-picker";
import {
  IntegrationConfigurator,
  isConfigurable,
} from "@/components/molecules/integration-configurator";
import { IntegrationLogo } from "@/components/molecules/integration-logo";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { useExternalAccounts } from "@/features/external-accounts/hooks/use-external-accounts";
import {
  isBookingProvider,
  type ExternalAccount,
} from "@/features/external-accounts/types";
import {
  fieldsFromAccount,
  INTEGRATION_OF,
  PROVIDER_OF,
} from "@/features/integrations/booking-accounts";
import {
  getIntegration,
  type BookingCategory,
} from "@/features/integrations/catalog";
import type { CheckedIntegration } from "@/features/integrations/types";
import { useIntegrationName } from "@/features/integrations/hooks/use-integration-name";
import { useTranslation } from "@/features/utils/i18n";
import { WarningCircleIcon } from "@phosphor-icons/react";
import { ArrowUpFromLine } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import type { CustomFieldProps } from "./dynamic-form";

/**
 * Integrations a tenant can still be on without being able to pick them: today
 * Provalliance, which is reached through a virtual-browser URL (see the backend's
 * `calendars/services/external/shortcuts.ts`) and set directly only by us. A tenant
 * stored on it must see it as its integration, not an empty field. It is listed
 * only while it is the current one: switching away is a choice like any other,
 * coming back to it is ours to make.
 */
const MANAGED = ["provalliance"];

const CATEGORIES: BookingCategory[] = [
  "builtin",
  "scheduling",
  "salon",
  "website",
];
const ALL = "all";

/** The picker's ids: one per account, plus "connect a new one" per integration. */
const accountOption = (id: string) => `account:${id}`;
const newOption = (integration: string) => `new:${integration}`;

interface Option {
  id: string;
  name: string;
  description: string;
  logo: string;
  category?: BookingCategory;
  tags?: string[];
}

const logoOf = (account: ExternalAccount) => account.provider;

/**
 * The booking software the agent books through — picked as **an account**. A
 * Provalliance salon, a Calendly account, a booking website are external accounts
 * (connected and checked once, listed on the integrations page with the Google
 * calendars), and this field only chooses one of them, or HeyUp's own calendar.
 * Picking also sets `integration` to the account's, which is what everything that
 * routes a booking reads.
 *
 * Connecting one is offered right in the list ("Nouveau compte …"): the setting
 * being enabled is exactly when the user learns they need the account.
 *
 * Configurations from before accounts were turned into accounts by migration 020,
 * so an integration that needs one and has none (or one since deleted) is simply
 * a module that cannot book, and says so.
 */
export function BookingIntegrationField({
  fieldName,
  fieldSchema,
  value,
  onChange,
  isRequired,
  inheritable = false,
  parentValue,
  onToggleInherit,
  formData = {},
  onChangeFields,
}: CustomFieldProps) {
  const { t } = useTranslation("tools");
  const company = useCurrentClient()?.client?.id;
  const { data: accounts = [] } = useExternalAccounts();
  const integrationName = useIntegrationName();
  const [open, setOpen] = useState(false);
  // The configurator open: a new account, or an account edited.
  const [configuring, setConfiguring] = useState<{
    integration: CheckedIntegration;
    account?: ExternalAccount;
    initialValues: Record<string, unknown>;
  } | null>(null);

  const label = fieldSchema.title || fieldName;
  const isInheriting = inheritable && value === undefined;
  const integration = String((isInheriting ? parentValue : value) ?? "");
  const accountId = String(formData.booking_account_id ?? "");

  const bookingAccounts = accounts.filter(
    (account) =>
      isBookingProvider(account.provider) &&
      account.capabilities?.capabilities?.includes("booking"),
  );
  const account = bookingAccounts.find((a) => a.id === accountId);
  const offered = (fieldSchema.enum ?? []).map(String);

  const set = (values: Record<string, unknown>) => {
    if (onChangeFields) onChangeFields(values);
    else onChange(values.integration);
  };

  /** Book through this account: its integration, and the account itself. */
  const bookThrough = (picked: ExternalAccount) =>
    set({
      integration:
        INTEGRATION_OF[picked.provider as keyof typeof INTEGRATION_OF],
      booking_account_id: picked.id,
    });

  const options: Option[] = [];
  const option = (o: Option) => options.push(o);
  if (offered.includes("internal")) {
    option({
      id: "internal",
      name: t("tools.integrations.internal.name"),
      description: t("tools.integrations.internal.tagline"),
      logo: "internal",
      category: "builtin",
    });
  }
  for (const a of bookingAccounts) {
    option({
      id: accountOption(a.id),
      name: a.name,
      description: a.provider_identifier,
      logo: logoOf(a),
      category: getIntegration(a.provider)?.bookingCategory,
      tags:
        a.status === "active" ? undefined : [t("tools.integrations.inactive")],
    });
  }
  for (const id of offered.filter(isConfigurable)) {
    option({
      id: newOption(id),
      name: t("tools.integrations.picker.newAccount", {
        name: integrationName(id),
      }),
      description: t(`tools.integrations.${id}.tagline`, ""),
      logo: id,
      category: getIntegration(id)?.bookingCategory,
    });
  }
  if (MANAGED.includes(integration)) {
    option({
      id: integration,
      name: integrationName(integration),
      description: "",
      logo: integration,
      category: getIntegration(integration)?.bookingCategory,
      tags: [t("tools.integrations.managed")],
    });
  }

  // What is selected: an account, HeyUp's calendar, a managed integration — or
  // an integration that needs an account and has none, which no option stands for.
  const accountless =
    !account && isConfigurable(integration) ? integration : undefined;
  const selectedId = account
    ? accountOption(account.id)
    : accountless
      ? undefined
      : integration;
  const selected = options.find((o) => o.id === selectedId);
  const categoryLabel = (o?: { category?: BookingCategory }) =>
    o?.category ? t(`tools.integrations.categories.${o.category}`) : undefined;

  const triggerLabel = selected?.name
    ? selected.name
    : accountless
      ? integrationName(accountless)
      : undefined;
  const triggerLogo = selected?.logo ?? accountless;

  const toggleInherit = () => {
    if (!onChangeFields) return onToggleInherit?.();
    // The integration and its account are one choice: inherited or not together.
    onChangeFields(
      isInheriting
        ? {
            integration: parentValue ?? "internal",
            booking_account_id: accountId,
          }
        : { integration: undefined, booking_account_id: undefined },
    );
  };

  const inheritToggle =
    inheritable && onToggleInherit ? (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={toggleInherit}
        className="h-6 px-2 text-xs"
      >
        <ArrowUpFromLine className="w-3 h-3 mr-1" />
        {isInheriting ? t("tools.form.override") : t("tools.form.inherit")}
      </Button>
    ) : null;

  const pick = (id: string) => {
    setOpen(false);
    if (id.startsWith("new:")) {
      const target = id.slice(4) as CheckedIntegration;
      setConfiguring({ integration: target, initialValues: {} });
    } else if (id.startsWith("account:")) {
      const picked = bookingAccounts.find((a) => accountOption(a.id) === id);
      if (picked) bookThrough(picked);
    } else {
      set({ integration: id, booking_account_id: "" });
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label
          // Inherited, the field is a read-only line: there is no control to point at.
          htmlFor={isInheriting ? undefined : fieldName}
          className={isInheriting ? "text-muted-foreground" : undefined}
        >
          {label}
          {isRequired && <span className="text-destructive ml-1">*</span>}
        </Label>
        {inheritToggle}
      </div>

      {isInheriting ? (
        <div className="flex items-center gap-3 text-sm text-muted-foreground p-3 bg-muted/50 rounded-md border border-dashed">
          <IntegrationLogo id={triggerLogo} size="sm" />
          <span>
            {t("tools.form.inheriting")}{" "}
            <strong>{triggerLabel ?? t("tools.form.notSet")}</strong>
          </span>
        </div>
      ) : (
        <div className="flex">
          <ChoicePickerTrigger
            id={fieldName}
            onClick={() => setOpen(true)}
            placeholder={t("tools.integrations.picker.placeholder")}
            leading={
              triggerLogo && <IntegrationLogo id={triggerLogo} size="xs" />
            }
            label={triggerLabel}
            meta={
              MANAGED.includes(integration)
                ? t("tools.integrations.managed")
                : (selected?.description ?? categoryLabel(selected))
            }
          />
        </div>
      )}

      {!isInheriting && account && (
        <Notice tone={account.status === "active" ? "neutral" : "warning"}>
          <span className="min-w-0 grow truncate text-sm">
            {account.status === "active"
              ? t("tools.integrations.summary.account", {
                  name: account.name,
                  identifier: account.provider_identifier,
                })
              : t("tools.integrations.summary.inactive", {
                  name: account.name,
                })}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              setConfiguring({
                integration:
                  INTEGRATION_OF[
                    account.provider as keyof typeof INTEGRATION_OF
                  ],
                account,
                initialValues: fieldsFromAccount(account),
              })
            }
          >
            {t("tools.integrations.summary.edit")}
          </Button>
        </Notice>
      )}

      {!isInheriting && accountless && (
        <Notice tone="warning">
          <span className="min-w-0 grow text-sm">
            {accountId
              ? t("tools.integrations.summary.missing")
              : t("tools.integrations.summary.noAccount")}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setOpen(true)}
          >
            {t("tools.integrations.summary.choose")}
          </Button>
        </Notice>
      )}

      {!isInheriting && (
        <p className="text-xs text-muted-foreground">
          {t("tools.integrations.summary.managedOn")}{" "}
          <Link
            to={`/${company}/settings/accounts`}
            className="underline underline-offset-2"
          >
            {t("tools.integrations.summary.accountsPage")}
          </Link>
        </p>
      )}

      {configuring && (
        <IntegrationConfigurator
          integration={configuring.integration}
          open={!!configuring}
          onOpenChange={(next) => !next && setConfiguring(null)}
          accountId={configuring.account?.id}
          initialValues={configuring.initialValues}
          onSaved={(id) => {
            // The list is read again behind the modal; until then the account is
            // known by what was just saved.
            bookThrough(
              configuring.account ??
                ({
                  id,
                  provider: PROVIDER_OF[configuring.integration],
                } as ExternalAccount),
            );
          }}
        />
      )}

      <ChoicePicker
        open={open}
        onOpenChange={setOpen}
        title={t("tools.integrations.picker.title")}
        description={t("tools.integrations.picker.description")}
      >
        <IntegrationPickerPanel
          options={options}
          value={selectedId || undefined}
          categoryLabel={categoryLabel}
          onCancel={() => setOpen(false)}
          onConfirm={pick}
        />
      </ChoicePicker>
    </div>
  );
}

function Notice({
  tone,
  children,
}: {
  tone: "neutral" | "warning";
  children: React.ReactNode;
}) {
  return (
    <div
      className={
        tone === "neutral"
          ? "flex items-center gap-3 rounded-md border bg-muted/30 p-3"
          : "flex items-center gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950"
      }
    >
      {tone === "warning" && (
        <WarningCircleIcon
          weight="fill"
          className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
        />
      )}
      {children}
    </div>
  );
}

function IntegrationPickerPanel({
  options,
  value,
  categoryLabel,
  onCancel,
  onConfirm,
}: {
  options: Option[];
  value?: string;
  categoryLabel: (o?: Option) => string | undefined;
  onCancel: () => void;
  onConfirm: (id: string) => void;
}) {
  const { t } = useTranslation("tools");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(ALL);

  // Only the kinds some option has: a filter that can only empty the list is not
  // a choice.
  const categories = CATEGORIES.filter((c) =>
    options.some((o) => o.category === c),
  );

  const query = search.trim().toLocaleLowerCase();
  const filtered = options.filter(
    (o) =>
      (category === ALL || o.category === category) &&
      (!query ||
        o.name.toLocaleLowerCase().includes(query) ||
        o.description.toLocaleLowerCase().includes(query)),
  );

  const filters: ChoiceFilter[] =
    categories.length > 1
      ? [
          {
            id: "category",
            label: t("tools.integrations.picker.category"),
            kind: "segmented",
            value: category,
            onChange: setCategory,
            options: [
              { value: ALL, label: t("tools.integrations.categories.all") },
              ...categories.map((c) => ({
                value: c,
                label: t(`tools.integrations.categories.${c}`),
              })),
            ],
          },
        ]
      : [];

  const items: ChoiceItem[] = filtered.map((o) => ({
    id: o.id,
    title: o.name,
    leading: <IntegrationLogo id={o.logo} />,
    meta: categoryLabel(o),
    description: o.description || undefined,
    tags: o.tags,
  }));

  return (
    <ChoicePickerPanel
      items={items}
      value={value}
      search={search}
      onSearchChange={setSearch}
      searchPlaceholder={t("tools.integrations.picker.search")}
      filters={filters}
      listLabel={t("tools.integrations.picker.title")}
      fitContent
      emptyText={t("tools.integrations.picker.empty")}
      onResetFilters={() => {
        setSearch("");
        setCategory(ALL);
      }}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}
